import React from 'react';
import { useQuoteStore } from '@/store/quoteStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const Step5CustomerDetails = () => {
  const { customerDetails, updateCustomerDetail, setCurrentStep } = useQuoteStore();

  const handleNext = () => {
    // Validate required fields
    if (!customerDetails.name || !customerDetails.email || !customerDetails.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerDetails.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setCurrentStep(7);
  };

  const handleBack = () => {
    setCurrentStep(5);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
      <h2 className="text-2xl md:text-3xl font-bold text-[#27292E] mb-2">
        Your Details
      </h2>
      <p className="text-gray-600 mb-8">
        Please provide your contact information so we can send you the quote.
      </p>

      <div className="space-y-5">
        <div>
          <Label htmlFor="name" className="text-sm font-medium text-gray-700 mb-2 block">
            Name or Company Name *
          </Label>
          <Input
            id="name"
            type="text"
            placeholder="Enter your name or company name"
            value={customerDetails.name}
            onChange={(e) => updateCustomerDetail('name', e.target.value)}
            className="h-14 text-lg border-2 focus:border-[#2F5E96] rounded-xl"
            data-testid="customer-name"
          />
        </div>

        <div>
          <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2 block">
            Email Address *
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="your.email@example.com"
            value={customerDetails.email}
            onChange={(e) => updateCustomerDetail('email', e.target.value)}
            className="h-14 text-lg border-2 focus:border-[#2F5E96] rounded-xl"
            data-testid="customer-email"
          />
        </div>

        <div>
          <Label htmlFor="phone" className="text-sm font-medium text-gray-700 mb-2 block">
            Phone Number *
          </Label>
          <Input
            id="phone"
            type="tel"
            placeholder="Enter your phone number"
            value={customerDetails.phone}
            onChange={(e) => updateCustomerDetail('phone', e.target.value)}
            className="h-14 text-lg border-2 focus:border-[#2F5E96] rounded-xl"
            data-testid="customer-phone"
          />
        </div>

        <div>
          <Label htmlFor="customer_name" className="text-sm font-medium text-gray-700 mb-2 block">
            Customer Name (Optional)
          </Label>
          <Input
            id="customer_name"
            type="text"
            placeholder="Enter customer name"
            value={customerDetails.customer_name || ''}
            onChange={(e) => updateCustomerDetail('customer_name', e.target.value)}
            className="h-14 text-lg border-2 focus:border-[#2F5E96] rounded-xl"
            data-testid="customer-customer-name"
          />
        </div>

        <div>
          <Label htmlFor="address" className="text-sm font-medium text-gray-700 mb-2 block">
            Customer Address (Optional)
          </Label>
          <Input
            id="address"
            type="text"
            placeholder="Enter customer address"
            value={customerDetails.address || ''}
            onChange={(e) => updateCustomerDetail('address', e.target.value)}
            className="h-14 text-lg border-2 focus:border-[#2F5E96] rounded-xl"
            data-testid="customer-address"
          />
        </div>

        <div>
          <Label htmlFor="start_date" className="text-sm font-medium text-gray-700 mb-2 block">
            Date Materials Required (Optional)
          </Label>
          <Input
            id="start_date"
            type="date"
            value={customerDetails.start_date || ''}
            onChange={(e) => updateCustomerDetail('start_date', e.target.value)}
            className="h-14 text-lg border-2 focus:border-[#2F5E96] rounded-xl"
            data-testid="customer-start-date"
          />
        </div>
      </div>

      <div className="flex gap-4 mt-8">
        <Button
          onClick={handleBack}
          variant="outline"
          className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#2F5E96] text-[#2F5E96] hover:bg-[#2F5E96]/5"
          data-testid="back-button-step6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleNext}
          className="flex-1 bg-[#2F5E96] hover:bg-[#254b7a] text-white h-14 text-lg font-bold rounded-xl transition-transform active:scale-95"
          data-testid="next-button-step6"
        >
          Review Quote
        </Button>
      </div>
    </div>
  );
};

export default Step5CustomerDetails;